import { useEffect, useMemo, useState } from "react";
import Button from "../../../../../Components/Common/Button/Button";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import { ProgressReportPrefill } from "../../../../../hooks/useConstructionProgressReport";
import Input from "Components/Common/Input/Input";
import NoPhotos from "Assets/Img/NoPhotos";
import EmptyState from "Components/Common/EmptyState";

interface DocState {
  id: string;
  isIncluded: boolean;
  description: string;
}

interface Props {
  prefill?: ProgressReportPrefill;
  initialDocuments?: DocState[];
  onDocumentsChange?: (docs: DocState[]) => void;
}

const Step6Photos = ({
  prefill,
  initialDocuments,
  onDocumentsChange,
}: Props) => {
  const rawDocs = prefill?.documents ?? [];

  const items = useMemo(
    () =>
      rawDocs.map((doc, i) => ({
        index: i,
        id: doc.id,
        url: doc.document.url,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefill?.documents],
  );

  const [docStates, setDocStates] = useState<DocState[]>(() => {
    if (initialDocuments && initialDocuments.length > 0)
      return initialDocuments;
    return rawDocs.map((doc) => ({
      id: doc.id,
      isIncluded: doc.isIncluded,
      description: doc.note ?? doc.description ?? "",
    }));
  });

  // Fallback: initialize when rawDocs arrives async (edge case)
  useEffect(() => {
    if (docStates.length === 0 && rawDocs.length > 0) {
      setDocStates(
        rawDocs.map((doc) => ({
          id: doc.id,
          isIncluded: doc.isIncluded,
          description: doc.note ?? doc.description ?? "",
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDocs.length]);

  useEffect(() => {
    onDocumentsChange?.(docStates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docStates]);

  const togglePhoto = (id: string) => {
    setDocStates((prev) =>
      prev.map((d) => (d.id === id ? { ...d, isIncluded: !d.isIncluded } : d)),
    );
  };

  const updateCaption = (id: string, value: string) => {
    setDocStates((prev) =>
      prev.map((d) => (d.id === id ? { ...d, description: value } : d)),
    );
  };

  const selectAll = (value: boolean) => {
    setDocStates((prev) => prev.map((d) => ({ ...d, isIncluded: value })));
  };

  const selectedCount = docStates.filter((d) => d.isIncluded).length;

  return (
    <div className="craScreen">
      <SectionHeader
        title="Progress Photos"
        subtitle="Select photos from FI submission to include in the report."
      />
      <Card>
        {items.length === 0 ? (
          <EmptyState
            noBorder
            icon={<NoPhotos />}
            title="No Photos Available"
            subtitle="No photos were captured during the field inspection for this
                draw."
          />
        ) : (
          <>
            <div className="photoToolbar">
              <p>
                {items.length} photos available —{" "}
                <strong>{selectedCount}</strong> selected
              </p>
              <div className="actions">
                <Button
                  className="btnSecondary"
                  onClick={() => selectAll(false)}
                >
                  Deselect All
                </Button>
                <Button
                  className="btnSecondary"
                  onClick={() => selectAll(true)}
                >
                  Select All
                </Button>
              </div>
            </div>
            <div className="photoGrid">
              {items.map((item) => {
                const state = docStates.find((d) => d.id === item.id);
                const selected = state?.isIncluded ?? false;
                const caption = state?.description ?? "";
                return (
                  <div
                    key={item.id}
                    className={`photoCard ${selected ? "selected" : ""}`}
                  >
                    <div className="thumb" onClick={() => togglePhoto(item.id)}>
                      {item.url ? (
                        <img
                          src={item.url}
                          alt={caption}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span className="num">{item.index + 1}</span>
                      )}
                      <span className={`check ${selected ? "show" : ""}`}>
                        ✓
                      </span>
                    </div>
                    <Input
                      value={caption}
                      className="photoDescInput"
                      name="photoDesc"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateCaption(item.id, e.target.value)
                      }
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default Step6Photos;
